/**
 * CollisionSystem.ts
 *
 * Centralized collision management for player, projectiles, and entities.
 * Uses solidEntities group (TileSprites with solid behavior) for ground collisions.
 *
 * Uses standard Arcade Physics overlap detection.
 * Line-collision projectiles (laser) move their AABB to the tip each frame,
 * so standard overlap works correctly.
 */

import Phaser from 'phaser';
import { Entity } from '../entities/base/Entity';
import { Unit } from '../entities/base/Unit';
import { Player } from '../entities/Player';
import { Bomb } from '../entities/hazards/Bomb';
import { Projectile } from '../projectiles/Projectile';
import { Allegiance } from '../weapons/DataManagerWeapon';
import { AoeZone } from './AoeZone';
import { audioManager } from '../core/CoreManagerAudio';
import { WalkerBehavior } from '../entities/behaviors/WalkerBehavior';
import { SolidBehavior } from '../entities/behaviors/SolidBehavior';

/**
 * AOE explosion event data
 */
export interface AoeExplosionEvent {
  x: number;
  y: number;
  radius: number;
  damage: number;
  duration: number;   // Duration in ms (0 = instantaneous)
  tint?: number;      // Color tint for visual
  allegiance: Allegiance;
  sourceTarget?: Entity;  // Entity that was directly hit (to exclude from AOE)
  attackType?: string;    // Attack type for weakTo immunity check
}

/**
 * CollisionSystem - Manages projectile collisions
 *
 * Features:
 * - solidEntities group containing all solid objects (TileSprites, Entities with SolidBehavior)
 * - Groups for enemy entities and structures
 * - Automatic collision detection between projectiles and targets
 * - Uses existing entitySpawned/entityDied events
 */
export class CollisionSystem {
  private scene: Phaser.Scene;

  // Groups of collidable entities
  private enemyEntities: Phaser.GameObjects.Group;
  private structures: Phaser.GameObjects.Group;
  private solidEntities: Phaser.GameObjects.Group;
  private hazards: Phaser.GameObjects.Group;

  // Stored colliders for cleanup
  private colliders: Phaser.Physics.Arcade.Collider[] = [];

  // Active AOE zones
  private activeAoeZones: Set<AoeZone> = new Set();

  // Track entities that recently hit solids (to prevent turnAround spam)
  private recentSolidCollisions: Map<Entity, number> = new Map();
  private readonly SOLID_COLLISION_COOLDOWN = 200; // ms

  constructor(scene: Phaser.Scene) {
    this.scene = scene;

    // Create groups for collidable entities
    this.enemyEntities = scene.add.group();
    this.structures = scene.add.group();
    this.solidEntities = scene.add.group();
    this.hazards = scene.add.group();

    // Listen for AOE explosion events
    this.scene.events.on('aoeExplosion', this.onAoeExplosion, this);

    // Cleanup on scene shutdown/destroy
    this.scene.events.once('shutdown', this.destroy, this);
    this.scene.events.once('destroy', this.destroy, this);
  }


  /**
   * Setup projectile colliders
   * Uses standard Arcade Physics overlap detection
   */
  setupProjectileColliders(
    player: Player,
    friendlyProjectiles: Phaser.GameObjects.Group,
    enemyProjectiles: Phaser.GameObjects.Group,
    destructibleEnemyProjectiles?: Phaser.GameObjects.Group
  ): void {
    // Friendly projectiles vs enemies
    this.colliders.push(
      this.scene.physics.add.overlap(
        friendlyProjectiles,
        this.enemyEntities,
        this.onProjectileHitEntity as Phaser.Types.Physics.Arcade.ArcadePhysicsCallback,
        undefined,
        this
      )
    );

    // Friendly projectiles vs structures
    this.colliders.push(
      this.scene.physics.add.overlap(
        friendlyProjectiles,
        this.structures,
        this.onProjectileHitEntity as Phaser.Types.Physics.Arcade.ArcadePhysicsCallback,
        undefined,
        this
      )
    );

    // Enemy projectiles vs player
    this.colliders.push(
      this.scene.physics.add.overlap(
        enemyProjectiles,
        player,
        this.onProjectileHitPlayer as Phaser.Types.Physics.Arcade.ArcadePhysicsCallback,
        undefined,
        this
      )
    );

    // ALL projectiles vs solidEntities (for destructOnFloor)
    this.colliders.push(
      this.scene.physics.add.overlap(
        friendlyProjectiles,
        this.solidEntities,
        this.onProjectileHitSolid as Phaser.Types.Physics.Arcade.ArcadePhysicsCallback,
        undefined,
        this
      )
    );
    this.colliders.push(
      this.scene.physics.add.overlap(
        enemyProjectiles,
        this.solidEntities,
        this.onProjectileHitSolid as Phaser.Types.Physics.Arcade.ArcadePhysicsCallback,
        undefined,
        this
      )
    );

    // Friendly projectiles vs destructible enemy projectiles
    if (destructibleEnemyProjectiles) {
      this.colliders.push(
        this.scene.physics.add.overlap(
          friendlyProjectiles,
          destructibleEnemyProjectiles,
          this.onProjectileHitDestructible as Phaser.Types.Physics.Arcade.ArcadePhysicsCallback,
          undefined,
          this
        )
      );
    }
  }

  /**
   * Setup solid entity colliders
   * Solid entities block player and NPCs from passing through
   */
  setupSolidColliders(player: Player): void {
    // Player vs solid entities (blocks movement)
    this.colliders.push(
      this.scene.physics.add.collider(
        player,
        this.solidEntities
      )
    );

    // Enemy/neutral entities (walkers) vs solid entities
    // When a walker hits a solid, it turns around
    this.colliders.push(
      this.scene.physics.add.collider(
        this.enemyEntities,
        this.solidEntities,
        this.onWalkerHitSolid as Phaser.Types.Physics.Arcade.ArcadePhysicsCallback,
        undefined,
        this
      )
    );
  }

  /**
   * Setup hazard colliders (bombs, traps)
   * Hazards damage player on contact
   */
  setupHazardColliders(player: Player): void {
    // Player vs hazards (bombs explode on contact)
    this.colliders.push(
      this.scene.physics.add.overlap(
        player,
        this.hazards,
        this.onPlayerHitHazard as Phaser.Types.Physics.Arcade.ArcadePhysicsCallback,
        undefined,
        this
      )
    );
  }

  /**
   * Handler: Player touches a hazard (bomb)
   */
  private onPlayerHitHazard(
    playerObj: Phaser.GameObjects.GameObject,
    hazardObj: Phaser.GameObjects.GameObject
  ): void {
    const player = playerObj as Player;
    const hazard = hazardObj as Entity;

    if (!hazard.active || !player.active) return;

    // Handle bomb collision - bomb explodes and damages player
    if (hazard instanceof Bomb) {
      const damage = hazard.damage;
      hazard.explode(false); // false = don't emit playerDamage event
      player.takeDamage(damage); // Apply damage directly
    }
  }

  /**
   * Handler: Walker hits a solid entity - turn around
   */
  private onWalkerHitSolid(
    walkerObj: Phaser.GameObjects.GameObject,
    _solidObj: Phaser.GameObjects.GameObject
  ): void {
    const walker = walkerObj as Entity;

    if (!walker.active) return;

    // Only turn around on HORIZONTAL collisions (hitting a wall)
    // NOT on vertical collisions (standing on floor)
    const body = walker.body as Phaser.Physics.Arcade.Body;
    if (!body) return;

    const isHorizontalCollision = body.touching.left || body.touching.right;
    if (!isHorizontalCollision) {
      return; // Vertical collision (floor/ceiling) - don't turn around
    }

    // Check cooldown to prevent turnAround spam
    const now = this.scene.time.now;
    const lastCollision = this.recentSolidCollisions.get(walker) || 0;

    if (now - lastCollision < this.SOLID_COLLISION_COOLDOWN) {
      return; // Still on cooldown
    }

    // Update cooldown timestamp
    this.recentSolidCollisions.set(walker, now);

    // If entity has WalkerBehavior, turn around
    const walkerBehavior = walker.getBehavior?.(WalkerBehavior);
    if (walkerBehavior) {
      walkerBehavior.turnAround();
    }
  }

  /**
   * Get solid entities group
   */
  getSolidEntities(): Phaser.GameObjects.Group {
    return this.solidEntities;
  }

  /**
   * Register any GameObject as solid (TileSprites, Entities with SolidBehavior)
   */
  registerSolid(obj: Phaser.GameObjects.GameObject): void {
    this.solidEntities.add(obj);
  }

  /**
   * Unregister a GameObject from solid group
   */
  unregisterSolid(obj: Phaser.GameObjects.GameObject): void {
    this.solidEntities.remove(obj, false, false);
  }

  /**
   * Check overlap with solid entities - for AbductableBehavior landing detection
   * @returns The first solid object that overlaps, or null if none
   */
  checkOverlapWithSolids(entity: Phaser.GameObjects.GameObject): Phaser.GameObjects.GameObject | null {
    const solids = this.solidEntities.getChildren();
    for (const solid of solids) {
      if (solid.active && this.scene.physics.overlap(entity, solid)) {
        return solid;
      }
    }
    return null;
  }

  /**
   * Get Y position of the nearest solid below a given position
   * Used by TractorBeam for burn mark placement, AbductableBehavior for landing
   * @returns Y position of the solid's top edge, or null if no solid found
   */
  getSolidYBelow(x: number, y: number): number | null {
    const solids = this.solidEntities.getChildren() as Phaser.GameObjects.Components.Transform[];
    let closestY: number | null = null;

    for (const solid of solids) {
      if (!solid.active) continue;

      // Get solid bounds - different handling for TileSprite vs Entity
      const solidObj = solid as Phaser.GameObjects.Sprite | Phaser.GameObjects.TileSprite;
      const solidTop = solidObj.y - solidObj.displayHeight * solidObj.originY;
      const solidLeft = solidObj.x - solidObj.displayWidth * solidObj.originX;
      const solidRight = solidLeft + solidObj.displayWidth;

      // Check if X is within solid's horizontal bounds
      if (x >= solidLeft && x <= solidRight) {
        // Check if solid is below the query point
        if (solidTop > y && (closestY === null || solidTop < closestY)) {
          closestY = solidTop;
        }
      }
    }
    return closestY;
  }

  /**
   * Get Y of nearest ground TileSprite below (solo suelo, ignora casas/entidades sólidas)
   */
  getGroundYBelow(x: number, y: number): number | null {
    const solids = this.solidEntities.getChildren() as Phaser.GameObjects.Components.Transform[];
    let closestY: number | null = null;

    for (const solid of solids) {
      if (!solid.active) continue;
      // Solo TileSprites (suelo). Ignorar entidades sólidas.
      if (!(solid instanceof Phaser.GameObjects.TileSprite)) continue;

      const solidObj = solid as Phaser.GameObjects.TileSprite;
      const solidTop = solidObj.y - solidObj.displayHeight * solidObj.originY;
      const solidLeft = solidObj.x - solidObj.displayWidth * solidObj.originX;
      const solidRight = solidLeft + solidObj.displayWidth;

      if (x >= solidLeft && x <= solidRight) {
        if (solidTop > y && (closestY === null || solidTop < closestY)) {
          closestY = solidTop;
        }
      }
    }

    return closestY;
  }

  /**
   * Register an entity in the appropriate collision group
   * Called when entitySpawned event is emitted
   */
  registerEntity(entity: Entity): void {
    const textureKey = entity.texture?.key ?? 'unknown';
    const body = entity.body as Phaser.Physics.Arcade.Body | null;
    // console.log(`[CollisionSystem] registerEntity called for ${textureKey}`, {
    //   allegiance: entity.allegiance,
    //   category: entity.config?.category,
    //   hasBody: !!body,
    //   bodyEnabled: body?.enable,
    //   bodySize: body ? { width: body.width, height: body.height } : null,
    // });

    // Add enemies AND neutrals to enemy group (player can shoot both)
    if (entity.allegiance === Allegiance.Enemy || entity.allegiance === Allegiance.Neutral) {
      this.enemyEntities.add(entity);
      // console.log(`[CollisionSystem] Added ${textureKey} to enemyEntities. Group size: ${this.enemyEntities.getLength()}`);
    }

    // Add structures to structures group
    if (entity.config?.category === 'structure') {
      this.structures.add(entity);
    }

    // Add hazards (bombs) to hazards group
    if (entity.config?.category === 'hazard') {
      this.hazards.add(entity);
    }

    // Note: Solid entities are registered via SolidBehavior.registerInCollisionSystem()
    // No need to handle config.solid here - the behavior handles everything
  }

  /**
   * Unregister an entity from collision groups
   * Called when entityDied event is emitted
   */
  unregisterEntity(entity: Entity): void {
    this.enemyEntities.remove(entity, false, false);
    this.structures.remove(entity, false, false);
    this.hazards.remove(entity, false, false);

    // If it was a solid, also remove from tractor blockers
    // Use hasBehavior() instead of config.solid for consistency with SolidBehavior
    if (entity.hasBehavior(SolidBehavior)) {
      this.scene.events.emit('tractorBlockerRemoved', entity);
    }

    this.solidEntities.remove(entity, false, false);
    this.recentSolidCollisions.delete(entity);
  }

  /**
   * Handler: Projectile hits an entity
   */
  private onProjectileHitEntity(
    projObj: Phaser.GameObjects.GameObject,
    entityObj: Phaser.GameObjects.GameObject
  ): void {
    const proj = projObj as Projectile;
    const entity = entityObj as Unit;

    if (!proj.active || !entity.active) return;

    // Check if entity has damage type restriction (weakTo)
    const weakTo = entity.config?.weakTo as string | undefined;
    if (weakTo && proj.definition.attackType !== weakTo) {
      // Entity is immune to this attack type

      // Play immunity sound effect
      audioManager.playSfx('sfx_immune', 'entity', this.scene);

      // Visual feedback: flash white/cyan briefly to indicate immunity
      entity.setTint(0x88ffff);
      this.scene.time.delayedCall(100, () => {
        if (entity.active) entity.clearTint();
      });

      // Projectile still hits (destroyed) but no damage
      proj.handleEntityCollision(entity);
      return;
    }

    // Apply damage if entity has takeDamage method
    if (typeof entity.takeDamage === 'function') {
      entity.takeDamage(proj.damage);
    }

    // Notify projectile of hit
    proj.handleEntityCollision(entity);
  }

  /**
   * Handler: Projectile hits the player
   */
  private onProjectileHitPlayer(
    projObj: Phaser.GameObjects.GameObject,
    playerObj: Phaser.GameObjects.GameObject
  ): void {
    const proj = projObj as Projectile;
    const player = playerObj as Player;

    if (!proj.active || !player.active) return;

    // Apply damage to player
    player.takeDamage(proj.damage);

    // Notify projectile of hit
    proj.handleEntityCollision(player);
  }

  /**
   * Handler: Projectile hits a solid (floor, walls, solid entities)
   */
  private onProjectileHitSolid(
    projObj: Phaser.GameObjects.GameObject,
    solidObj: Phaser.GameObjects.GameObject
  ): void {
    const proj = projObj as Projectile;

    if (!proj.active) return;

    // Check if solid is an Entity with allegiance (not a TileSprite/tilemap)
    // Projectiles should NOT collide with solid entities of the same allegiance
    const solid = solidObj as Entity;
    if (solid.allegiance !== undefined && solid.allegiance === proj.allegiance) {
      // Same allegiance - ignore collision (e.g., enemy projectile hitting enemy solid)
      return;
    }

    // Also check if this solid is the shooter itself (by entityId)
    if (proj.shooterId !== undefined && (solid as Entity & { entityId?: number }).entityId === proj.shooterId) {
      // Projectile hitting its own shooter - ignore
      return;
    }

    proj.handleGroundCollision();
  }

  /**
   * Handler: Friendly projectile hits a destructible enemy projectile
   */
  private onProjectileHitDestructible(
    friendlyObj: Phaser.GameObjects.GameObject,
    destructibleObj: Phaser.GameObjects.GameObject
  ): void {
    const friendly = friendlyObj as Projectile;
    const destructible = destructibleObj as Projectile;

    if (!friendly.active || !destructible.active) return;

    // Deal damage to destructible projectile
    destructible.takeDamage(friendly.damage);

    // Friendly projectile also gets destroyed on hit
    friendly.handleEntityCollision();
  }


  /**
   * Get enemy entities group
   */
  getEnemyEntities(): Phaser.GameObjects.Group {
    return this.enemyEntities;
  }

  /**
   * Get structures group
   */
  getStructures(): Phaser.GameObjects.Group {
    return this.structures;
  }

  /**
   * Handler: AOE explosion damages all entities within radius
   */
  private onAoeExplosion(event: AoeExplosionEvent): void {
    const { x, y, radius, damage, duration, tint, allegiance, sourceTarget, attackType } = event;

    // If duration > 0, create a persistent AOE zone
    if (duration > 0) {
      const zone = new AoeZone(this.scene, {
        x,
        y,
        radius,
        damage,
        duration,
        allegiance,
        tint,
        sourceTarget,
        attackType,
      });

      zone.setEnemyGroup(this.enemyEntities);
      this.activeAoeZones.add(zone);

      // Remove from set when destroyed
      zone.once('destroy', () => {
        this.activeAoeZones.delete(zone);
      });
      return;
    }

    // Instantaneous AOE (duration = 0)
    const entities = this.enemyEntities.getChildren() as Unit[];
    let hitCount = 0;

    for (const entity of entities) {
      if (!entity.active) continue;

      // Skip the entity that was directly hit (already took full damage)
      if (sourceTarget && entity === sourceTarget) continue;

      // Check if entity has damage type restriction (weakTo)
      const weakTo = entity.config?.weakTo as string | undefined;
      if (weakTo && attackType !== weakTo) {
        // Entity is immune to this attack type
        continue;
      }

      // Check if entity is within AOE radius
      const distance = Phaser.Math.Distance.Between(x, y, entity.x, entity.y);
      if (distance <= radius) {
        // Apply AOE damage
        if (typeof entity.takeDamage === 'function') {
          entity.takeDamage(damage);
          hitCount++;
        }
      }
    }

  }

  /**
   * Update active AOE zones
   */
  updateAoeZones(time: number, delta: number): void {
    // Iterate over copy to safely allow removal during iteration
    for (const zone of [...this.activeAoeZones]) {
      zone.update(time, delta);
    }
  }

  /**
   * Find nearest enemy entity from a position
   * @param x Source X position
   * @param y Source Y position
   * @param onlyHostile If true, only returns entities with allegiance === Allegiance.Enemy (excludes neutrals)
   * @param maxRange Maximum search range (default: no limit)
   * @returns The nearest enemy entity, or undefined if none found
   */
  findNearestEnemy(
    x: number,
    y: number,
    onlyHostile: boolean = false,
    maxRange: number = Infinity
  ): Entity | undefined {
    let nearestEntity: Entity | undefined;
    let nearestDistance = maxRange;

    const entities = this.enemyEntities.getChildren() as Entity[];

    for (const entity of entities) {
      if (!entity.active) continue;

      // Filter by allegiance if onlyHostile is true
      if (onlyHostile && entity.allegiance !== Allegiance.Enemy) {
        continue;
      }

      const distance = Phaser.Math.Distance.Between(x, y, entity.x, entity.y);

      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestEntity = entity;
      }
    }

    return nearestEntity;
  }

  /**
   * Cleanup
   */
  destroy(): void {
    // Prevent double destroy
    if (this.isDestroyed) return;
    this.isDestroyed = true;

    // Remove event listeners
    this.scene.events.off('aoeExplosion', this.onAoeExplosion, this);

    // Remove all colliders (check if still valid)
    for (const collider of this.colliders) {
      if (collider && collider.active !== undefined) {
        collider.destroy();
      }
    }
    this.colliders = [];

    // Destroy active AOE zones (check if still valid)
    for (const zone of this.activeAoeZones) {
      if (zone && zone.scene) {
        zone.destroy();
      }
    }
    this.activeAoeZones.clear();

    // Just clear group references - don't destroy the groups
    // Check if group is still valid before clearing (may already be destroyed by Phaser)
    if (this.enemyEntities?.children) this.enemyEntities.clear(false, false);
    if (this.structures?.children) this.structures.clear(false, false);
    if (this.solidEntities?.children) this.solidEntities.clear(false, false);
    if (this.hazards?.children) this.hazards.clear(false, false);

    // Clear collision cooldown tracking
    this.recentSolidCollisions.clear();
  }

  private isDestroyed = false;
}
